// YtreChannelSwitchModal.jsx — Sep 14 2026 (Randy), rebuilt Sep 15 2026.
//
// "clicking the 'load into YTRE', is it possible to auto pop the
//  'reconnect to the right channel' auth window, allow the user to re-auth
//  to the other channel. then after it accepts the re-auth, it loads the
//  URL associated?"
//
// Sep 15 2026 (Randy: "Identity and key switching needs to be allowed and
// working and hardened to not break from consistent and frequent changing").
// The first build always re-consented into the silo's channel1 slot, which
// OVERWROTE the row — the hat being left behind lost its channel id and its
// refresh token, so every switch cost a Google popup and silently erased the
// other identity. Channels now live in an append-only registry with their own
// credentials, so the common case is a straight credential swap: no popup, no
// consent screen, nothing destroyed. The Google round-trip is only offered
// for a hat we have never seen sign in.

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, ArrowsLeftRight, CheckCircle } from "@phosphor-icons/react";
import { ytApi } from "@/lib/ytApi";

export default function YtreChannelSwitchModal({
  siloId, row, connectedName, onClose, onSwitched,
}) {
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [known, setKnown] = useState(null);   // registry hit, or null
  const [checking, setChecking] = useState(true);
  const popupRef = useRef(null);
  const wantId = row?.interaction_channel_id || row?.unread_activity?.interaction_channel_id || "";
  const wantName = row?.interaction_channel_name
    || row?.unread_activity?.interaction_channel_name
    || "another channel";

  // Do we already hold a sign-in for the hat that owns this conversation?
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const chans = await ytApi.myChannels();
        if (cancelled) return;
        const hit = (chans || []).find(
          (c) => c.youtube_channel_id === wantId && c.can_switch,
        );
        setKnown(hit || null);
      } catch {
        setKnown(null);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [wantId]);

  // Fallback path only: the OAuth callback page posts back to its opener.
  useEffect(() => {
    const onMsg = (ev) => {
      if (!ev?.data || ev.data.type !== "yt-oauth-complete") return;
      setWaiting(false);
      setBusy(false);
      if (ev.data.ok) {
        toast.success(`Switched. Loading ${row?.video_title || "the video"}…`);
        onSwitched && onSwitched();
      } else {
        toast.error(ev.data.msg || "Google sign-in didn't complete.");
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [row, onSwitched]);

  const switchSilently = async () => {
    setBusy(true);
    try {
      await ytApi.switchChannel(siloId, "channel1", wantId);
      toast.success(`Now posting as ${wantName}. Loading ${row?.video_title || "the video"}…`);
      onSwitched && onSwitched();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Couldn't switch channel.");
      setBusy(false);
    }
  };

  const switchViaGoogle = async () => {
    // Sep 26, 2026 (Randy — "pressing reconnect or quick shift does not pull
    // up a new window on mobile; desktop popped up just fine"). The popup MUST
    // be opened synchronously inside the tap. Mobile Safari and Android Chrome
    // only honour window.open while the user's gesture is still live; after an
    // await for the auth URL that gesture is gone and the call is refused —
    // which is exactly why this worked on desktop and did nothing on a phone.
    const popup = window.open("", "yt-oauth", "width=520,height=680,left=120,top=80");
    if (!popup) {
      toast.error("Your browser blocked the sign-in window — allow pop-ups for this site, then tap again.");
      return;
    }
    popupRef.current = popup;
    try {
      popup.document.write(
        "<!doctype html><title>Google sign-in</title>"
        + "<body style='font:14px -apple-system,sans-serif;padding:24px;color:#333'>"
        + "Opening Google sign-in…</body>",
      );
    } catch (_e) { /* cross-origin already navigated — fine */ }
    setBusy(true);
    try {
      const d = await ytApi.authUrl(siloId, "channel1");
      const url = d?.authorization_url;
      if (!url) throw new Error("No authorization URL came back.");
      popup.location.href = url;
      setWaiting(true);
    } catch (e) {
      try { popup.close(); } catch (_e) { /* ignore */ }
      toast.error(e.response?.data?.detail || e.message || "Couldn't start the switch.");
      setBusy(false);
    }
  };

  const instant = !!known;

  return (
    <div
      className="fixed inset-0 z-[250] bg-black/50 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4"
      onClick={onClose}
      data-testid="ytre-channel-switch-modal"
    >
      <div
        className="bg-[#FDFCF8] rounded-2xl w-full max-w-md shadow-2xl border hairline overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b hairline flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">Wrong hat</div>
            <h2 className="font-serif-display text-lg text-[#1C1B1A]">Switch channel, then load</h2>
          </div>
          <button
            onClick={onClose}
            data-testid="ytre-channel-switch-close"
            className="p-1 rounded hover:bg-[#F0F1EE]"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <p className="text-[13px] text-[#1C1B1A] leading-relaxed">
            This conversation belongs to{" "}
            <strong className="font-semibold">{wantName}</strong>, but this silo is
            connected as{" "}
            <strong className="font-semibold">{connectedName || "another channel"}</strong>.
          </p>

          {checking ? (
            <p className="text-[11.5px] text-[#6B6A68] italic" data-testid="ytre-channel-switch-checking">
              Checking which sign-ins we hold…
            </p>
          ) : instant ? (
            <p
              className="text-[11.5px] text-[#2F6B3A] flex items-start gap-1.5"
              data-testid="ytre-channel-switch-instant-note"
            >
              <CheckCircle size={13} weight="fill" className="mt-0.5 shrink-0" />
              <span>
                {wantName} is already on file — switching is instant, and{" "}
                {connectedName || "the current channel"} stays connected for every
                other silo.
              </span>
            </p>
          ) : (
            <p
              className="text-[11.5px] text-[#6B4838] italic"
              data-testid="ytre-channel-switch-firsttime-note"
            >
              We haven't seen {wantName} sign in yet. Do it once here and every switch
              to it after this is instant.
            </p>
          )}

          <button
            onClick={instant ? switchSilently : switchViaGoogle}
            disabled={busy || waiting || checking}
            data-testid="ytre-channel-switch-confirm"
            className="w-full text-[12.5px] px-3 py-2.5 rounded-md bg-[#2F6B3A] text-white hover:bg-[#245029] disabled:opacity-50 flex items-center justify-center gap-1.5 font-medium"
          >
            <ArrowsLeftRight size={13} weight="bold" />
            {waiting
              ? "Waiting for Google sign-in…"
              : busy
                ? "Switching…"
                : `Switch to ${wantName} & load`}
          </button>

          <button
            onClick={() => { onSwitched && onSwitched(); }}
            data-testid="ytre-channel-switch-skip"
            className="w-full text-[11.5px] text-[#6B4838] hover:text-[#1C1B1A]"
          >
            Load anyway as {connectedName || "the current channel"}
          </button>
          <button
            onClick={onClose}
            data-testid="ytre-channel-switch-cancel"
            className="w-full text-[11px] text-[#6B6A68] hover:text-[#1C1B1A] pt-0.5"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
